import { useCallback, useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./CareerLeads.css";
import { getCareerLeads, getResumePdf } from "../utils/auth";
const CareerLeadsTable = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const itemsPerPage = 5;

  // 🔹 Fetch Data
const fetchLeads = useCallback(async () => {
  try {
    setLoading(true);
    setError("");

    const res = await getCareerLeads();

    const data = res.data;

    // ✅ SAFE NORMALIZATION
    if (Array.isArray(data)) {
      setLeads(data);
    } else if (Array.isArray(data?.data)) {
      setLeads(data.data);
    } else if (Array.isArray(data?.items)) {
      setLeads(data.items);
    } else {
      setLeads([]);
    }

  } catch (err) {
    console.error(err);
    setError("Failed to fetch career leads");
    setLeads([]);
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // 🔍 Search
const filteredLeads = useMemo(() => {
  if (!Array.isArray(leads)) return [];

  return leads.filter((lead) =>
    Object.values(lead).some((val) =>
      String(val).toLowerCase().includes(search.toLowerCase())
    )
  );
}, [leads, search]);
  // 📊 Sorting
  const sortedLeads = useMemo(() => {
    let data = [...filteredLeads];
    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = String(a[sortConfig.key] ?? "").toLowerCase();
        const bVal = String(b[sortConfig.key] ?? "").toLowerCase();

        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      });
    }
    return data;
  }, [filteredLeads, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  // 📄 Pagination
  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const paginatedLeads = sortedLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 📥 Export
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(sortedLeads);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Career Leads");

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(new Blob([buffer]), "career-leads.xlsx");
  };

  // 📅 Format Date
  const formatDate = (date) =>
    date ? new Date(date).toLocaleString("en-IN") : "";

  // ✅ Boolean
  const renderStatus = (val) => {
    if (val === null) return <span className="badge status-pill bg-secondary">N/A</span>;
    return (
      <span className={`badge status-pill ${val ? "bg-success" : "bg-danger"}`}>
        {val ? "Yes" : "No"}
      </span>
    );
  };

  // 👁 Preview Resume
  const handlePreviewResume = async (resumeNameCreated) => {
  try {
    setShowModal(true);
    setPdfLoading(true);

    const res = await getResumePdf(resumeNameCreated);

    if (pdfUrl) URL.revokeObjectURL(pdfUrl);

    const url = URL.createObjectURL(res.data);
    setPdfUrl(url);
  } catch (err) {
    alert("Failed to load resume");
  } finally {
    setPdfLoading(false);
  }
};

  // 📎 Download
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "resume.pdf";
    link.click();
  };

  return (
    <div>

      <div className="dashboard-toolbar">
        <input
          className="form-control w-auto"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button className="btn btn-primary" onClick={exportToExcel} disabled={sortedLeads.length === 0}>
          Export Excel
        </button>
      </div>

      {loading && (
        <div className="dashboard-loading">
          <div className="spinner-border text-primary"></div>
          <p className="mt-2">Loading...</p>
        </div>
      )}

      {!loading && error && (
        <div className="dashboard-error">{error}</div>
      )}

      {!loading && leads.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle dashboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th className="sortable-heading" onClick={() => handleSort("fullName")}>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th className="sortable-heading" onClick={() => handleSort("position")}>Position</th>
                  <th>Resume</th>
                  <th>Owner</th>
                  <th>User</th>
                  <th className="sortable-heading" onClick={() => handleSort("createdAt")}>Created</th>
                </tr>
              </thead>

              <tbody>
                {paginatedLeads.map((lead, i) => (
                  <tr key={lead.id}>
                    <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                    <td>{lead.fullName}</td>
                    <td>{lead.email}</td>
                    <td>{lead.phone}</td>
                    <td>{lead.location}</td>
                    <td>{lead.position}</td>

                    <td>
                      <button
                        className="btn btn-link p-0 text-primary"
                        onClick={() =>
                          handlePreviewResume(lead.resumeNameCreated)
                        }
                      >
                        {lead.resumeNameUploaded}
                      </button>
                    </td>

                    <td>{renderStatus(lead.notifiedOwner)}</td>
                    <td>{renderStatus(lead.notifiedUser)}</td>
                    <td>{formatDate(lead.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dashboard-pagination">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`btn ${
                  currentPage === i + 1
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}

      {!loading && leads.length === 0 && (
        <div className="dashboard-empty">No career leads found.</div>
      )}

      {showModal && (
        <div className="custom-modal">
          <div className="custom-modal-content">

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6>Resume Preview</h6>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleDownload}
                  disabled={!pdfUrl}
                >
                  Download
                </button>

                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => {
                    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                    setShowModal(false);
                    setPdfUrl("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="modal-body-area">
              {pdfLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                  <p className="mt-2">Loading PDF...</p>
                </div>
              ) : (
                <iframe
                  src={pdfUrl}
                  title="PDF"
                  width="100%"
                  height="500px"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerLeadsTable;
